import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter } from 'rxjs';
import { Timeframe } from '../entities/Timeframe.js';

@customElement('bh-prompt-timeframe-modify')
export class BHPromptTimeframeModify extends TrDialog<void> {
  @property() mode = new RXController<Maybe<'add' | 'edit'>>(this, undefined);
  @property({ attribute: false }) timeframe = new RXController<Maybe<Timeframe>>(this, undefined);

  @state() private readonly _form = new FormController(this, this.timeframe.observable.pipe(filter(Boolean)), {
    success: async doc => {
      if (this.mode.value === 'add') {
        await doc.create();
      }
      if (this.mode.value === 'edit') {
        await doc.save();
      }

      // FIXME: this should propably not be neceassary, but whithout it workshops in BHPageAdminEinrichtungWorkshopsModify do not show new Timeframe values
      // TODO: check weather this is still needed
      // EntityStore.drop(Product as any);

      return this.close();
    },
    error: err => {
      console.log(err);
    },
  });

  override render() {
    if (!this.mode.value || this.timeframe.isLoading || !this.timeframe.value || !this._form.document) {
      return html` <x-loading-state no-wrapper></x-loading-state> `;
    }

    return html`
      <x-flex space="md">
        ${this.mode.value === 'add'
          ? html`
              <x-h2>
                ${this._translation.translate('timeframe', 'singular' as any)} ${this._translation.translate('create')}
              </x-h2>
            `
          : html`
              <x-h2>
                ${this._translation.translate('timeframe', 'singular' as any)} ${this._translation.translate('edit')}
              </x-h2>
            `}

        <tr-input ${this._form.field('name')} placeholder="Name"></tr-input>

        <x-date-picker-dropdown
          .value=${this._form.document.from ?? null}
          placeholder=${this._translation.translate('from')}
          @value-picked=${(e: ValueEvent<Date | null>) => {
            const from = e.detail.value ?? undefined;
            const doc = this._form.document;
            if (!doc) throw new Error('should not happen');

            if (!from) return;
            doc.from = from;
            const currentTill = doc.till;
            if (!currentTill || from > currentTill) {
              doc.till = from;
              this.requestUpdate();
            }
          }}
        ></x-date-picker-dropdown>

        <x-date-picker-dropdown
          .value=${this._form.document.till ?? null}
          @value-picked=${(e: ValueEvent<Date | null>) => {
            const till = e.detail.value ?? undefined;
            if (till) this._form.document!.till = till;
          }}
          placeholder=${this._translation.translate('till')}
        ></x-date-picker-dropdown>

        <x-date-picker-dropdown
          .value=${this._form.document.publishDate ?? null}
          @value-picked=${(e: ValueEvent<Date | null>) => {
            this._form.document!.publishDate = e.detail.value ?? undefined;
          }}
          placeholder="${this._translation.translate('field', 'publishDate' as any)}*"
        ></x-date-picker-dropdown>

        <x-flex horizontal space-between crossaxis-center space="md" wrap>
          <x-button mode="outline" @click=${() => this.cancel()}>${this._translation.translate('discard')}</x-button>
          <x-button @click=${() => this._form.validate()}>${this._translation.translate('save')}</x-button>
        </x-flex>
      </x-flex>
    `;
  }

  static add(institutionID: string) {
    return this.showPopup({ props: { mode: 'add', timeframe: new Timeframe({ institutionID }) } });
  }

  static edit(timeframe: Timeframe) {
    return this.showPopup({ props: { mode: 'edit', timeframe } });
  }

  override async cancel(returnVal?: any): Promise<void> {
    await this.close();
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('background', this.colors.white);
    wrapperElem.style.setProperty('border-radius', this.sizes.borderRadius);
    wrapperElem.style.setProperty('width', `min(550px, 95%)`);
    wrapperElem.style.setProperty('padding', this.spacing.lg);
    return wrapperElem;
  }

  static override get element_name() {
    return 'bh-prompt-timeframe-modify';
  }
}
